<script lang="ts">
  import { page } from '$app/state';
  import { afterNavigate } from '$app/navigation';
  import { Button } from '@cio/ui/base/button';
  import Menu from '@lucide/svelte/icons/menu';
  import X from '@lucide/svelte/icons/x';
  import ArrowUpRight from '@lucide/svelte/icons/arrow-up-right';
  import { fade, fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import { onMount } from 'svelte';
  import MotionAction from '$lib/components/landing/effects/motion-action.svelte';
  import MotionToggle from '$lib/components/site/motion-toggle.svelte';
  import { NAV_LINKS, SIGN_IN_URL } from '$lib/utils/site';
  import { isMotionAllowed } from '$lib/utils/motion-state.svelte';

  let header = $state<HTMLElement>();
  let open = $state(false);
  let scrolled = $state(false);
  let menuButton = $state<HTMLButtonElement | null>(null);
  let transitionScale = $state(0);

  afterNavigate(() => {
    open = false;
  });

  function isCurrent(href: string) {
    if (href.includes('#')) return false;

    return page.url.pathname === href;
  }

  function closeMenu() {
    open = false;
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape' || !open) return;

    open = false;
    menuButton?.focus();
  }

  function moveGlassLight(event: PointerEvent) {
    if (!header || event.pointerType !== 'mouse' || !isMotionAllowed()) return;

    const bounds = header.getBoundingClientRect();
    const horizontalPosition = ((event.clientX - bounds.left) / bounds.width) * 100;
    header.style.setProperty('--glass-x', `${horizontalPosition}%`);
  }

  function resetGlassLight() {
    header?.style.removeProperty('--glass-x');
  }

  function updateScrolled() {
    scrolled = scrollY > 24;
  }

  onMount(() => {
    if (!header) return;

    const surface = header;
    surface.addEventListener('pointermove', moveGlassLight, { passive: true });
    surface.addEventListener('pointerleave', resetGlassLight);
    window.addEventListener('scroll', updateScrolled, { passive: true });
    updateScrolled();
    return () => {
      surface.removeEventListener('pointermove', moveGlassLight);
      surface.removeEventListener('pointerleave', resetGlassLight);
      window.removeEventListener('scroll', updateScrolled);
    };
  });

  function toggleMenu() {
    transitionScale = isMotionAllowed() ? 1 : 0;
    open = !open;
  }
</script>

<svelte:window onkeydown={handleKeydown} />
<a class="skip-link" href="#main-content">Skip to content</a>
<div class="announcement">
  <span class="announcement-dot" aria-hidden="true"></span>A shared home for your institution.
  <a href="/product">Meet Edvello <ArrowUpRight size={12} /></a>
</div>
{#if open}<button
    type="button"
    class="menu-scrim"
    aria-label="Close menu"
    tabindex="-1"
    onclick={closeMenu}
    transition:fade={{ duration: 220 * transitionScale }}
  ></button>{/if}
<header class="site-header" class:menu-open={open} class:scrolled bind:this={header}>
  <nav class="site-nav" aria-label="Main navigation">
    <a href="/" aria-label="Edvello home" class="brand-link"
      ><img src="/brand/edvello-logo-horizontal.svg" width="124" height="35" alt="Edvello" /></a
    >
    <div class="desktop-links">
      {#each NAV_LINKS as link}<a href={link.href} aria-current={isCurrent(link.href) ? 'page' : undefined}
          >{link.label}</a
        >{/each}
    </div>
    <div class="nav-actions">
      <a class="sign-in" href={SIGN_IN_URL}>Sign in</a><MotionAction compact onActivate={closeMenu} /><Button
        variant="secondary"
        size="icon"
        class="mobile-toggle"
        bind:ref={menuButton}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        aria-controls="mobile-navigation"
        onclick={toggleMenu}
        ><span class="toggle-icon" class:open
          >{#if open}<X size={20} />{:else}<Menu size={20} />{/if}</span
        ></Button
      >
    </div>
  </nav>
  {#if open}<nav
      id="mobile-navigation"
      class="mobile-navigation"
      aria-label="Mobile navigation"
      transition:fly={{ y: -10, duration: 260 * transitionScale, easing: cubicOut }}
    >
      <ul>
        {#each NAV_LINKS as link, index}<li
            in:fly={{
              y: 14,
              duration: 420 * transitionScale,
              delay: (60 + index * 45) * transitionScale,
              easing: cubicOut
            }}
          >
            <a href={link.href} aria-current={isCurrent(link.href) ? 'page' : undefined} onclick={closeMenu}
              ><span class="link-index">0{index + 1}</span>{link.label}<ArrowUpRight size={18} /></a
            >
          </li>{/each}
      </ul>
      <div
        class="mobile-menu-footer"
        in:fly={{ y: 14, duration: 420 * transitionScale, delay: 320 * transitionScale, easing: cubicOut }}
      >
        <a class="mobile-sign-in" href={SIGN_IN_URL}>Sign in</a>
        <MotionToggle variant="inline" />
      </div>
    </nav>{/if}
  <noscript
    ><nav class="no-script-nav" aria-label="Additional navigation">
      {#each NAV_LINKS as link}<a href={link.href}>{link.label}</a>{/each}<a href={SIGN_IN_URL}>Sign in</a>
    </nav></noscript
  >
</header>

<style>
  .announcement {
    position: relative;
    z-index: 41;
    background: #173f98;
    color: #eaf1ff;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 36px;
    padding: 8px 16px;
    font-size: 11px;
    line-height: 1.5;
  }
  .announcement-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f4edac;
    box-shadow: 0 0 0 0 #f4edac80;
    animation: announcement-pulse 2.6s ease-out infinite;
  }
  @keyframes announcement-pulse {
    0% {
      box-shadow: 0 0 0 0 #f4edac90;
    }
    70%,
    100% {
      box-shadow: 0 0 0 7px #f4edac00;
    }
  }
  .announcement a {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-left: 8px;
    color: #fff;
    font-weight: 500;
    white-space: nowrap;
  }
  .announcement a:hover {
    text-decoration: underline;
  }
  .menu-scrim {
    position: fixed;
    inset: 0;
    z-index: 39;
    border: 0;
    padding: 0;
    background: #0d1b3a40;
    -webkit-backdrop-filter: blur(3px);
    backdrop-filter: blur(3px);
    cursor: default;
  }
  .site-header {
    position: sticky;
    top: 14px;
    z-index: 40;
    isolation: isolate;
    border: 1px solid #ffffffb8;
    border-radius: 22px;
    width: min(1060px, calc(100% - 48px));
    margin: 18px auto 0;
    background:
      radial-gradient(ellipse 240px 110px at var(--glass-x, 28%) 0%, #ffffff80, transparent 85%),
      linear-gradient(125deg, #ffffff54, #ffffff08 48%, #d5e5ff38), #f8faffb8;
    -webkit-backdrop-filter: blur(24px) saturate(165%) brightness(1.06);
    backdrop-filter: blur(24px) saturate(165%) brightness(1.06);
    box-shadow:
      0 12px 32px -20px #142b6170,
      inset 0 1px 0 #ffffffed,
      inset 1px 0 0 #ffffff80,
      inset 0 -1px 0 #bed1ed80;
    transition:
      box-shadow 400ms cubic-bezier(0.16, 1, 0.3, 1),
      background-color 400ms;
  }
  .site-header.scrolled {
    box-shadow:
      0 18px 40px -22px #142b6190,
      inset 0 1px 0 #ffffffed,
      inset 1px 0 0 #ffffff80,
      inset 0 -1px 0 #bed1ed80;
  }
  .site-header::after {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: inherit;
    border: 1px solid #ffffff45;
    pointer-events: none;
    z-index: -1;
  }
  .site-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    min-height: 64px;
    padding: 0 12px 0 21px;
    transition: min-height 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .scrolled .site-nav {
    min-height: 58px;
  }
  .brand-link {
    flex-shrink: 0;
    transition: transform 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .brand-link:hover {
    transform: translateY(-1px);
  }
  .brand-link img {
    display: block;
    width: 124px;
    height: auto;
  }
  .desktop-links {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .desktop-links a {
    position: relative;
    padding: 9px 12px;
    border-radius: 999px;
    transition:
      color 200ms,
      background 250ms;
  }
  .desktop-links a::after {
    content: '';
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: 4px;
    height: 1.5px;
    border-radius: 2px;
    background: currentColor;
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .desktop-links a:hover::after,
  .desktop-links a[aria-current='page']::after {
    transform: scaleX(1);
    transform-origin: left;
  }
  .desktop-links a,
  .sign-in {
    font-size: 12px;
    color: #263b5c;
    font-weight: 500;
    white-space: nowrap;
  }
  .desktop-links a:hover,
  .desktop-links a[aria-current='page'],
  .sign-in:hover {
    color: #1447e6;
  }
  .nav-actions {
    display: flex;
    align-items: center;
    gap: 21px;
  }
  :global(.mobile-toggle) {
    display: none !important;
  }
  .toggle-icon {
    display: grid;
    place-items: center;
    transition: rotate 350ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .toggle-icon.open {
    rotate: 90deg;
  }
  .mobile-navigation {
    padding: 4px 16px 16px;
    border-top: 1px solid #ffffff70;
  }
  .mobile-navigation ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .mobile-navigation li + li {
    border-top: 1px solid #dbe3f280;
  }
  .mobile-navigation li a {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 54px;
    font-size: 17px;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: #1d3355;
  }
  .mobile-navigation li a[aria-current='page'] {
    color: #1447e6;
  }
  .mobile-navigation li a :global(svg) {
    margin-left: auto;
    color: #7c8eac;
    transition: transform 300ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .mobile-navigation li a:active :global(svg) {
    transform: translate(2px, -2px);
  }
  .link-index {
    width: 22px;
    color: #8a9ab5;
    font-size: 10px;
    font-variant-numeric: tabular-nums;
  }
  .mobile-menu-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 10px;
    padding: 12px 14px;
    border-radius: 14px;
    background: #edf2fc;
    color: #4c6387;
  }
  .mobile-sign-in {
    display: inline-flex;
    align-items: center;
    min-height: 40px;
    font-size: 14px;
    font-weight: 550;
    color: #1447e6;
  }
  .no-script-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    padding: 12px 24px;
    font-size: 13px;
  }
  .skip-link {
    position: fixed;
    left: 20px;
    top: -80px;
    z-index: 100;
    padding: 16px;
    background: #1447e6;
    color: white;
    border-radius: 8px;
  }
  .skip-link:focus {
    top: 12px;
  }
  @media (max-width: 960px) {
    .desktop-links a {
      padding-inline: 8px;
    }
    .desktop-links a::after {
      left: 8px;
      right: 8px;
    }
    .nav-actions {
      gap: 15px;
    }
    .site-nav {
      gap: 14px;
    }
  }
  @media (max-width: 860px) {
    .desktop-links,
    .sign-in {
      display: none;
    }
    :global(.mobile-toggle) {
      display: inline-flex !important;
      width: 38px !important;
      height: 38px !important;
      background: transparent !important;
      color: #3d5477 !important;
      box-shadow: none !important;
    }
    .nav-actions {
      gap: 7px;
    }
  }
  @media (max-width: 760px) {
    .announcement {
      min-height: 32px;
      font-size: 10px;
      gap: 8px;
    }
    .site-header {
      width: calc(100% - 24px);
      margin-top: 12px;
      top: 10px;
      border-radius: 18px;
    }
    .site-nav {
      min-height: 58px;
      padding: 0 9px 0 14px;
      gap: 10px;
    }
    .scrolled .site-nav {
      min-height: 56px;
    }
    .brand-link img {
      width: 105px;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .announcement-dot {
      animation: none;
    }
  }
  :global(html[data-motion='paused']) .announcement-dot {
    animation: none;
  }
  @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
    .site-header {
      background: #f8fafff5;
    }
  }
  @media (prefers-reduced-transparency: reduce) {
    .site-header {
      background: #f8faff;
      -webkit-backdrop-filter: none;
      backdrop-filter: none;
    }
  }
  @media (forced-colors: active) {
    .site-header {
      background: Canvas;
      border-color: ButtonText;
      box-shadow: none;
      -webkit-backdrop-filter: none;
      backdrop-filter: none;
    }
    .desktop-links a,
    .sign-in,
    .mobile-navigation a {
      color: LinkText;
    }
  }
</style>
